import React, { useContext, useCallback, useMemo, useEffect, useState } from 'react';
import { CurrentPageContext, useHistoryState } from 'app/home/general-components/HistoryStackContext';
import FullPageTransition from 'app/home/general-components/FullPageTransition';
import { LocalizationContext } from 'app/localization/Context';

import Main from './main/Main';
import SelectDevice, { SelectScenario } from './select-device/SelectDevice';
import SelectAction from './select-action/SelectAction';
import SelectEventType from './select-event-type/SelectEventType';
import SpecialTypePage from './special-types/SpecialType';
import ThermostatSpecialPage from './thermostat-special/ThermostatSpecial';
import RemoteSpecialPage from './remote-special/RemoteSpecial';
import { BindActionType, BindConditionType } from 'app/model/Bind';
import { getBindInfo } from 'app/model/BindData';
import { getTimezone } from 'app/localization/Zone';

import './CreateBind.scss'

export default function CreateOrEditBind({ gateway }) {
    const l = useContext(LocalizationContext);

    const p = useContext(CurrentPageContext);

    const editMode = p.state.editMode;
    const editBindBase = p.state.editBindBase;

    const basePreBind = useMemo(() => {
        if (editMode === true) {
            const [conditionInfos, actionInfos] = getBindInfo(editBindBase, gateway.devices, gateway.scenarios, l);
            return {
                id: editBindBase.id,
                name: editBindBase.name,
                timezone: ((editBindBase.timezone !== undefined) && (editBindBase.timezone !== "")) ? editBindBase.timezone : "Asia/Tehran",
                delay: editBindBase.delay,
                duration_enabled: editBindBase.duration_enabled,
                duration_from_time: editBindBase.duration_from_time,
                duration_end_time: editBindBase.duration_end_time,
                day_of_week: editBindBase.day_of_week !== undefined ?
                    editBindBase.day_of_week : [true, true, true, true, true, true, true],
                conditionInfos: conditionInfos,
                actionInfos: actionInfos,
            };
        } else {
            return {
                name: "",
                timezone: getTimezone(),
                delay: 0,
                duration_enabled: false,
                duration_from_time: undefined,
                duration_end_time: undefined,
                day_of_week: [true, true, true, true, true, true, true],
                conditionInfos: [],
                actionInfos: [],
            };
        }
    }, [editMode, editBindBase, gateway.devices, gateway.scenarios, l]);

    // const [preBind, setPreBind] = useState(basePreBind);
    // const [pageData, setPageData] = useState({ name: 'main' });
    // const [inverse, isBackInProgress] = useState(false);

    const { delayedCurrentState, pushState, back, setLockState, numberOfStates } =
        useHistoryState(p.id, {
            preBind: basePreBind,
            pageData: { name: 'main' }
        });
    const currentState = delayedCurrentState;

    const preBind = currentState.preBind;
    const pageData = currentState.pageData;
    const setPreBind = useCallback((pb) => {
        pushState({
            preBind: pb,
            pageData
        });
    }, [pageData, pushState]);
    const setPageData = useCallback((pd) => {
        pushState({
            preBind,
            pageData: pd
        });
    }, [preBind, pushState]);

    const dismissHandler = useCallback(() => {
        setLockState(true);
        setTimeout(() => {
            back(true);
            setTimeout(() => { // fix a bug in safari: two consecutive backs will result in only perfoming the second one
                p.dismiss();
            });
        });
    }, [back, p, setLockState]);

    const [{ inverse }, setlastnNumberOfStates] = useState({ inverse: false, lastNumberOfStates: 0 });
    useEffect(() => {
        // console.log("numberOfStates", numberOfStates);
        setlastnNumberOfStates((x) => {
            return { inverse: numberOfStates < x.lastNumberOfStates, lastNumberOfStates: numberOfStates }
        })
    }, [numberOfStates]);

    const [bindName, setBindName] = useState(preBind.name);
    useEffect(() => {
        setBindName(preBind.name);
    }, [preBind.name]);

    const onFinish = useCallback((_preBind) => {
        // conditions: باید ایونت‌های مختلف هر دستگاه با هم ترکیب بشن
        const conditions = [];
        const macsInConditions = [];
        for (let i = 0; i < _preBind.conditionInfos.length; i++) {
            const info = _preBind.conditionInfos[i];
            const mac = info.data.device_mac;
            if ((info.data.condition_type === BindConditionType.device) && (mac !== undefined)) {
                if (!macsInConditions.includes(mac)) {
                    macsInConditions.push(mac);
                }
            }
        }
        for (let i = 0; i < macsInConditions.length; i++) {
            const mac = macsInConditions[i];
            const distilledData = {
                condition_type: BindConditionType.device,
                device_mac: mac,
                device_type: undefined,
                device_events: {},
            }
            for (let j = 0; j < _preBind.conditionInfos.length; j++) {
                const info = _preBind.conditionInfos[j];
                if (info.data.device_mac === mac) {
                    const notIntrestedKeys = ["device_mac", "device_type", "condition_type"];
                    for (const key in info.data) {
                        if (Object.prototype.hasOwnProperty.call(info.data, key) && !notIntrestedKeys.includes(key)) {
                            const value = info.data[key];
                            distilledData.device_events[key] = value;
                        }
                        if (key === "device_type") {
                            distilledData.device_type = info.data["device_type"];
                        }
                    }
                }
            }
            conditions.push(distilledData);
        }
        const otherConditionInfos = _preBind.conditionInfos.filter(condInfo => (
            (condInfo.data.condition_type !== BindConditionType.device)
        ));
        for (let i = 0; i < otherConditionInfos.length; i++) {
            const condInfo = otherConditionInfos[i];
            conditions.push(condInfo.data);
        }

        // action: باید ایونت‌های مختلف هر دستگاه با هم ترکیب بشن
        const actions = [];
        const macsInActions = [];
        for (let i = 0; i < _preBind.actionInfos.length; i++) {
            const info = _preBind.actionInfos[i];
            const mac = info.data.device_mac;
            if ((info.data.action_type === BindActionType.device) && (mac !== undefined)) {
                if (!macsInActions.includes(mac)) {
                    macsInActions.push(mac);
                }
            }
        }
        for (let i = 0; i < macsInActions.length; i++) {
            const mac = macsInActions[i];
            const distilledData = {
                action_type: BindActionType.device,
                device_mac: mac,
                device_type: undefined,
                device_state: {},
            }
            for (let j = 0; j < _preBind.actionInfos.length; j++) {
                const info = _preBind.actionInfos[j];
                if (info.data.device_mac === mac) {
                    const notIntrestedKeys = ["device_mac", "device_type", "action_type"];
                    for (const key in info.data) {
                        if (Object.prototype.hasOwnProperty.call(info.data, key) && !notIntrestedKeys.includes(key)) {
                            const value = info.data[key];
                            distilledData.device_state[key] = value;
                        }
                        if (key === "device_type") {
                            distilledData.device_type = info.data["device_type"];
                        }
                    }
                }
            }
            actions.push(distilledData);
        }
        const otherActionInfos = _preBind.actionInfos.filter(actInfo => (
            (actInfo.data.action_type !== BindActionType.device)
        ));
        for (let i = 0; i < otherActionInfos.length; i++) {
            const actInfo = otherActionInfos[i];
            actions.push(actInfo.data);
        }

        if (editMode) {
            gateway.editBind({
                id: _preBind.id,
                name: _preBind.name,
                timezone: _preBind.timezone,
                delay: _preBind.delay,
                duration_enabled: _preBind.duration_enabled,
                duration_from_time: _preBind.duration_from_time,
                duration_end_time: _preBind.duration_end_time,
                day_of_week: _preBind.day_of_week,
                conditions: conditions,
                actions: actions,
            });
        } else {
            gateway.createNewBind({
                name: _preBind.name,
                timezone: _preBind.timezone,
                delay: _preBind.delay,
                duration_enabled: _preBind.duration_enabled,
                duration_from_time: _preBind.duration_from_time,
                duration_end_time: _preBind.duration_end_time,
                day_of_week: _preBind.day_of_week,
                conditions: conditions,
                actions: actions,
            });
        }
    }, [gateway, editMode]);

    return (
        <div className="create-bind-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />

            <FullPageTransition disableOnFirstTime inverse={inverse}>
                <Main
                    editMode={editMode}
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler} onFinish={onFinish}
                    bindName={bindName} setBindName={setBindName}
                    negotiatedProtocol={gateway.negotiatedProtocol}
                    isVisible={pageData.name === "main"}
                    key="create-bind-main" />

                <SelectEventType
                    negotiatedProtocol={gateway.negotiatedProtocol}
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "select-event-type"}
                    key="create-bind-select-event-type" />

                <SpecialTypePage
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "special-type"}
                    key="create-bind-special-type-page" />

                <SelectDevice
                    negotiatedProtocol={gateway.negotiatedProtocol}
                    devices={gateway.devices}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "select-device"}
                    key="create-bind-select-device" />

                <SelectScenario
                    scenarios={gateway.scenarios}
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "select-scenario"}
                    key="create-bind-select-scenario" />

                <SelectAction
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "select-action"}
                    key="create-bind-select-actions" />

                <ThermostatSpecialPage
                    devices={gateway.devices}
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "thermostat-special"}
                    key="create-bind-thermostat-special-page" />

                <RemoteSpecialPage
                    devices={gateway.devices}
                    preBind={preBind} setPreBind={setPreBind}
                    pageData={pageData} setPageData={setPageData}
                    pushState={pushState} back={back}
                    dismissHandler={dismissHandler}
                    isVisible={pageData.name === "remote-special"}
                    key="create-bind-remote-special-page" />
            </FullPageTransition>
        </div>
    )

}